import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import InfoIcon from '../../svgs/InfoIcon';
import { DialogText, DialogTitle } from '../common/dialogStyles';
import Modal from '../common/Modal';

type Props = {
  closeModal: () => void;
};

const RetrySavingModal: React.FC<Props> = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { closeModal } = props;
  const error = videoCreator.savingError!;
  const handleRetrySaving = async () => {
    if (error.isBackground) {
      await videoCreator.retryBackgroundSave();
    } else {
      const resetTimeline = false;
      const withRenderer = true;
      await videoCreator.saveStoryAndVideo(false, withRenderer, resetTimeline);
    }
  };

  const errorMessage =
    'humanMessage' in error ? (error.humanMessage as string) : 'Unknown error';

  return (
    <Modal isOpen closeModal={closeModal}>
      <Wrapper>
        <Heading>
          <InfoIcon /> <span>Saving failed</span>
        </Heading>
        <Message>{errorMessage}</Message>
        <Buttons>
          <CancelButton onClick={closeModal}>Cancel</CancelButton>
          <SaveButton onClick={handleRetrySaving}>Retry</SaveButton>
        </Buttons>
      </Wrapper>
    </Modal>
  );
});

export default RetrySavingModal;

const Wrapper = styled.div`
  width: 459px;
  height: 260px;
  border-radius: 16px;
  border: 1px solid var(--border);
  padding: 48px 24px;
  box-sizing: border-box;
  background: var(--background);
`;

const Heading = styled(DialogTitle)`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Message = styled(DialogText)`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: 16px;
`;

const Buttons = styled.div`
  margin-top: 32px;
  display: flex;
  height: 48px;
  gap: 8px;
`;

const CancelButton = styled.div`
  box-sizing: border-box;
  flex: 1;
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid var(--destructive);
  cursor: pointer;

  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);

  color: var(--destructive);
`;

const SaveButton = styled(CancelButton)`
  background: var(--destructive);
  color: var(--background);
`;
